import { existsSync, mkdirSync, readdirSync, readFileSync, statSync, writeFileSync } from "node:fs";
import { basename, dirname, extname, join, relative } from "node:path";
import chalk from "chalk";
import type { CaiConfig } from "../types.js";
import { extractExports, extractImports, type ModuleExport } from "./regex.js";

export type { ModuleExport } from "./regex.js";
export { extractExports, extractImports } from "./regex.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SKIP_DIRS = new Set([
  "node_modules", ".git", "dist", "build", ".next", ".nuxt", "__pycache__",
  ".venv", "venv", "target", "vendor", ".cai", ".claude", "coverage",
  ".turbo", ".cache",
]);

const TS_EXTENSIONS = new Set([".ts", ".tsx", ".js", ".jsx"]);
const CODE_EXTENSIONS = new Set([".ts", ".tsx", ".js", ".jsx", ".py", ".go", ".rs"]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ModuleFile {
  relPath: string;
  exports: ModuleExport[];
  imports: string[];
}

export interface CodexResult {
  outputPath: string;
  moduleCount: number;
  exportCount: number;
  lines: number;
  written: boolean;
}

export interface RepoBriefResult {
  outputPath: string;
  lines: number;
  written: boolean;
}

// ---------------------------------------------------------------------------
// File walker
// ---------------------------------------------------------------------------

function walk(dir: string): string[] {
  const results: string[] = [];
  let entries;
  try {
    entries = readdirSync(dir, { withFileTypes: true });
  } catch {
    return results;
  }
  for (const entry of entries) {
    if (entry.name.startsWith(".")) continue;
    if (SKIP_DIRS.has(entry.name)) continue;
    if (entry.isSymbolicLink()) continue;
    const full = join(dir, entry.name);
    if (entry.isDirectory()) {
      for (const f of walk(full)) results.push(f);
    } else if (entry.isFile()) {
      if (entry.name.endsWith(".d.ts")) continue;
      if (entry.name.endsWith(".min.js")) continue;
      if (entry.name.endsWith(".map")) continue;
      const ext = extname(entry.name);
      if (CODE_EXTENSIONS.has(ext)) {
        results.push(full);
      }
    }
  }
  return results;
}

// ---------------------------------------------------------------------------
// Import resolution + reference counting
// ---------------------------------------------------------------------------

function resolveImportToFile(
  imp: string,
  fromRelPath: string,
  projectRoot: string,
  knownPaths: Set<string>
): string | null {
  const fromDir = dirname(join(projectRoot, fromRelPath));
  const base = join(fromDir, imp);
  const rel = relative(projectRoot, base);

  if (knownPaths.has(rel)) return rel;

  // TS ESM convention: .js imports map to .ts files
  if (rel.endsWith(".js")) {
    const tsVariant = rel.slice(0, -3) + ".ts";
    if (knownPaths.has(tsVariant)) return tsVariant;
    const tsxVariant = rel.slice(0, -3) + ".tsx";
    if (knownPaths.has(tsxVariant)) return tsxVariant;
  }

  for (const suffix of [".ts", ".tsx", ".js", ".jsx", "/index.ts", "/index.js"]) {
    const candidate = rel + suffix;
    if (knownPaths.has(candidate)) return candidate;
  }

  return null;
}

function buildReferenceCount(files: ModuleFile[], projectRoot: string): Map<string, number> {
  const counts = new Map<string, number>();
  const knownPaths = new Set<string>();
  for (const f of files) {
    counts.set(f.relPath, 0);
    knownPaths.add(f.relPath);
  }

  for (const f of files) {
    for (const imp of f.imports) {
      if (!imp.startsWith(".")) continue;
      const resolved = resolveImportToFile(imp, f.relPath, projectRoot, knownPaths);
      if (resolved) {
        counts.set(resolved, (counts.get(resolved) ?? 0) + 1);
      }
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

function renderModules(files: ModuleFile[], projectRoot: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const refCounts = buildReferenceCount(files, projectRoot);
  const totalExports = files.reduce((sum, f) => sum + f.exports.length, 0);

  const lines: string[] = [
    `<!-- generated -->`,
    `# Code Map (${today})`,
    `# ${files.length} files · ${totalExports} exports`,
    "",
  ];

  const groups = new Map<string, ModuleFile[]>();
  for (const f of files) {
    const dir = dirname(f.relPath);
    if (!groups.has(dir)) groups.set(dir, []);
    groups.get(dir)!.push(f);
  }

  for (const groupFiles of groups.values()) {
    groupFiles.sort((a, b) => (refCounts.get(b.relPath) ?? 0) - (refCounts.get(a.relPath) ?? 0));
  }

  for (const [group, groupFiles] of groups) {
    lines.push(`## ${group}`);

    for (const f of groupFiles) {
      const fns = f.exports.filter((e) => e.kind === "fn" || e.kind === "class");
      const methods = f.exports.filter((e) => e.kind === "method");
      const types = f.exports.filter((e) => e.kind === "type");
      const refs = refCounts.get(f.relPath) ?? 0;

      if (fns.length === 0 && types.length === 0) continue;

      const fileName = basename(f.relPath);
      const refTag = refs > 0 ? ` (${refs} refs)` : "";
      lines.push(`${fileName}${refTag}`);

      const MAX_FNS = 8;
      for (const ex of fns.slice(0, MAX_FNS)) {
        const marker = ex.kind === "class" ? "(c)" : "fn ";
        const ret = ex.returns ? ` -> ${ex.returns}` : "";
        lines.push(`  ${marker} ${ex.name}${ex.detail}${ret}`);
      }
      if (fns.length > MAX_FNS) lines.push(`  +${fns.length - MAX_FNS} more`);

      for (const m of methods.slice(0, 6)) {
        const ret = m.returns ? ` -> ${m.returns}` : "";
        lines.push(`    .${m.name}${m.detail}${ret}`);
      }
      if (methods.length > 6) lines.push(`    +${methods.length - 6} more methods`);

      if (types.length > 0) {
        for (const t of types.slice(0, 5)) {
          if (t.fields && t.fields.length > 0) {
            lines.push(`  type ${t.name} { ${t.fields.join("; ")} }`);
          } else {
            lines.push(`  type ${t.name}`);
          }
        }
        if (types.length > 5) lines.push(`  +${types.length - 5} more types`);
      }
    }

    lines.push("");
  }

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Shared module collection
// ---------------------------------------------------------------------------

function getScanDirs(projectRoot: string, explicit?: string[]): string[] {
  const dirs = (explicit ?? []).map((d) => join(projectRoot, d));
  if (dirs.length === 0) {
    for (const candidate of ["src", "lib", "utils"]) {
      const full = join(projectRoot, candidate);
      if (existsSync(full)) dirs.push(full);
    }
  }
  return dirs;
}

async function collectModuleFiles(
  projectRoot: string,
  scanDirs: string[],
  opts: { useTreeSitter?: boolean; requireFnOrClass?: boolean } = {}
): Promise<ModuleFile[]> {
  const { useTreeSitter = false, requireFnOrClass = true } = opts;

  const allFiles: string[] = [];
  for (const dir of scanDirs) {
    for (const f of walk(dir)) allFiles.push(f);
  }

  let treeSitterExtract: ((content: string, ext: string) => Promise<ModuleExport[] | null>) | null = null;
  if (useTreeSitter) {
    try {
      const ts = await import("./tree-sitter.js");
      treeSitterExtract = ts.extractExportsTreeSitter;
    } catch {
      // tree-sitter not available — regex fallback only
    }
  }

  const moduleFiles: ModuleFile[] = [];
  for (const absPath of allFiles) {
    let content: string;
    try {
      content = readFileSync(absPath, "utf-8");
    } catch {
      continue;
    }

    if (content.length > 1_000_000) continue;

    const ext = extname(absPath);
    let exports: ModuleExport[] | null = null;

    if (treeSitterExtract) {
      try {
        exports = await treeSitterExtract(content, ext);
      } catch {
        // fall through to regex
      }
    }

    if (!exports && TS_EXTENSIONS.has(ext)) {
      exports = extractExports(content);
    }

    if (!exports || exports.length === 0) continue;

    const imports = extractImports(content);
    if (requireFnOrClass && !exports.some((e) => e.kind === "fn" || e.kind === "class" || e.kind === "type")) {
      continue;
    }

    moduleFiles.push({
      relPath: relative(projectRoot, absPath),
      exports,
      imports,
    });
  }

  moduleFiles.sort((a, b) => a.relPath.localeCompare(b.relPath));
  return moduleFiles;
}

// ---------------------------------------------------------------------------
// Main runner
// ---------------------------------------------------------------------------

export async function runCodex(config: CaiConfig, opts: { scanDirs?: string[] } = {}): Promise<CodexResult> {
  const projectRoot = config.projectRoot;
  const scanDirs = getScanDirs(projectRoot, opts.scanDirs);
  const moduleFiles = await collectModuleFiles(projectRoot, scanDirs, { useTreeSitter: true });

  const content = renderModules(moduleFiles, projectRoot);

  const outputDir = join(config.scaffoldRoot, "codex");
  mkdirSync(outputDir, { recursive: true });
  const outputPath = join(outputDir, "modules.md");

  let written = false;
  const existing = existsSync(outputPath) ? readFileSync(outputPath, "utf-8") : null;
  if (existing !== content) {
    writeFileSync(outputPath, content, "utf-8");
    written = true;
  }

  const exportCount = moduleFiles.reduce((sum, f) => sum + f.exports.length, 0);

  return {
    outputPath,
    moduleCount: moduleFiles.length,
    exportCount,
    lines: content.split("\n").length,
    written,
  };
}

// ---------------------------------------------------------------------------
// CLI printers
// ---------------------------------------------------------------------------

export function printCodexResult(result: CodexResult): void {
  if (result.written) {
    console.log(
      chalk.green("  ✓") +
      ` ${chalk.bold("modules.md")} written — ${result.moduleCount} modules · ${result.exportCount} exports · ${result.lines} lines`
    );
    console.log(chalk.dim(`    ${result.outputPath}`));
  } else {
    console.log(
      chalk.dim("  ─") +
      ` modules.md up to date (${result.moduleCount} modules · ${result.exportCount} exports)`
    );
  }
}

// ---------------------------------------------------------------------------
// Repo Brief
// ---------------------------------------------------------------------------

export async function runRepoBrief(config: CaiConfig): Promise<RepoBriefResult> {
  const projectRoot = config.projectRoot;
  const scanDirs = getScanDirs(projectRoot);
  const moduleFiles = await collectModuleFiles(projectRoot, scanDirs, { requireFnOrClass: false });

  const refCounts = buildReferenceCount(moduleFiles, projectRoot);
  const knownPaths = new Set(moduleFiles.map((m) => m.relPath));

  const lines: string[] = [
    "<!-- generated -->",
    "# Repo Graph",
    "",
  ];

  // Top referenced files
  const ranked = [...refCounts.entries()]
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  if (ranked.length > 0) {
    lines.push("## Most Referenced");
    for (const [file, count] of ranked.slice(0, 15)) {
      lines.push(`${file}  (${count} refs)`);
    }
    lines.push("");
  }

  // Import edges
  lines.push("## Import Graph");
  const edgesBySource = new Map<string, string[]>();
  for (const f of moduleFiles) {
    const targets: string[] = [];
    for (const imp of f.imports) {
      if (!imp.startsWith(".")) continue;
      const resolved = resolveImportToFile(imp, f.relPath, projectRoot, knownPaths);
      if (resolved) targets.push(resolved);
    }
    if (targets.length > 0) {
      edgesBySource.set(f.relPath, [...new Set(targets)]);
    }
  }

  const sortedSources = [...edgesBySource.entries()]
    .sort((a, b) => (refCounts.get(b[0]) ?? 0) - (refCounts.get(a[0]) ?? 0));

  for (const [source, targets] of sortedSources.slice(0, 30)) {
    const shortTargets = targets.map((t) => basename(t, extname(t))).slice(0, 5);
    const suffix = targets.length > 5 ? ` +${targets.length - 5}` : "";
    lines.push(`${source} -> ${shortTargets.join(", ")}${suffix}`);
  }
  if (sortedSources.length > 30) lines.push(`+${sortedSources.length - 30} more`);
  lines.push("");

  const content = lines.join("\n");

  const outputDir = join(config.scaffoldRoot, "codex");
  mkdirSync(outputDir, { recursive: true });
  const outputPath = join(outputDir, "repo-brief.md");

  let written = false;
  const existing = existsSync(outputPath) ? readFileSync(outputPath, "utf-8") : null;
  if (existing !== content) {
    writeFileSync(outputPath, content, "utf-8");
    written = true;
  }

  return { outputPath, lines: content.split("\n").length, written };
}

export function printRepoBriefResult(result: RepoBriefResult): void {
  if (result.written) {
    console.log(
      chalk.green("  ✓") +
      ` ${chalk.bold("repo-brief.md")} written — ${result.lines} lines`
    );
    console.log(chalk.dim(`    ${result.outputPath}`));
  } else {
    console.log(
      chalk.dim("  ─") +
      ` repo-brief.md up to date (${result.lines} lines)`
    );
  }
}

// ---------------------------------------------------------------------------
// Staleness check
// ---------------------------------------------------------------------------

export function isCodexStale(config: CaiConfig): boolean {
  const codexDir = join(config.scaffoldRoot, "codex");
  const modulesPath = join(codexDir, "modules.md");
  const briefPath = join(codexDir, "repo-brief.md");

  if (!existsSync(modulesPath) || !existsSync(briefPath)) return true;

  const oldestCodex = Math.min(
    statSync(modulesPath).mtimeMs,
    statSync(briefPath).mtimeMs
  );

  for (const candidate of ["src", "lib", "utils"]) {
    const dir = join(config.projectRoot, candidate);
    if (!existsSync(dir)) continue;
    if (newestMtime(dir) > oldestCodex) return true;
  }

  return false;
}

function newestMtime(dir: string, depth = 0): number {
  if (depth > 4) return 0;
  let newest = 0;
  let entries;
  try {
    entries = readdirSync(dir, { withFileTypes: true });
  } catch {
    return 0;
  }
  for (const entry of entries) {
    if (entry.name.startsWith(".")) continue;
    if (SKIP_DIRS.has(entry.name)) continue;
    const full = join(dir, entry.name);
    try {
      const mtime = statSync(full).mtimeMs;
      if (mtime > newest) newest = mtime;
      if (entry.isDirectory()) {
        const sub = newestMtime(full, depth + 1);
        if (sub > newest) newest = sub;
      }
    } catch {
      continue;
    }
  }
  return newest;
}
